import { TableLoader } from 'components/TableLoader';
import { getColumnId, TableSettings } from 'components/TableSettings';
import { TotalCount } from 'components/TotalCount';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { WorkOrderChangeResultData, WorkOrderChangeSeasonData } from 'gql/graphql';
import { getTagLabel } from 'pages/Project/UerProject/WorkOrder/workOrderConstants';
import { getSummaryTableAdditionalClassName } from 'pages/Project/UerProject/WorkOrder/WorkOrderSummaryTable/helpers';
import { Grid, GridItem, Loader, Table } from 'uikit';

import type React from 'react';
import { useMemo } from 'react';

import { workOrderChangeResultGql } from '../WorkOrderChangeQueries';

import { useOrderChangeSummaryTableRows } from './hooks/useOrderChangeSummaryTableRows';
import { useWorkOrderChangeSummaryColumns } from './hooks/useWorkOrderChangeSummaryColumns';

interface IWorkOrderChangeSummaryTable {
    workOrderChangeId: number;
    costRounding: number;
    workOrderChangeSeasons: WorkOrderChangeSeasonData[];
}

export const WorkOrderChangeSummaryTable: React.FC<IWorkOrderChangeSummaryTable> = (props) => {
    const { workOrderChangeId, costRounding, workOrderChangeSeasons } = props;
    const variables = useMemo(() => ({ workOrderChangeId }), [workOrderChangeId]);

    const [workOrderResultData, workOrderResultStatus] = useRequest({
        requestId: 'workOrderChangeResult',
        query: workOrderChangeResultGql,
        variables,
        withEffect: Boolean(workOrderChangeId),
    });

    const isLoading = workOrderResultStatus === RequestStatus.FETCHING;
    const { columns, seasons, onTableSettingsConfirm, isColumnVisible } = useWorkOrderChangeSummaryColumns({
        workOrderChangeId,
        data: workOrderResultData?.uerWorkOrderChangeResult as WorkOrderChangeResultData[],
        costRounding,
        workOrderChangeSeasons,
    });

    const visibleColumns = useMemo(
        () => columns?.filter((column) => isColumnVisible(getColumnId(column))),
        [columns, isColumnVisible],
    );

    const { rows, groupsAvailable } = useOrderChangeSummaryTableRows(
        workOrderResultData?.uerWorkOrderChangeResult as WorkOrderChangeResultData[],
        seasons as WorkOrderChangeSeasonData[],
    );

    return (
        <>
            <Grid cols={2} className="TableSettings">
                <GridItem>
                    <TotalCount totalCount={rows.length ? rows.length - groupsAvailable : 0} />
                </GridItem>
                <GridItem style={{ display: 'flex', justifyContent: 'right' }}>
                    <TableSettings
                        columns={columns}
                        onConfirm={onTableSettingsConfirm}
                        isColumnVisible={isColumnVisible}
                    />
                </GridItem>
            </Grid>
            {isLoading && <Loader />}
            {!isLoading && !!rows.length && (
                <Table
                    columns={visibleColumns}
                    rows={rows}
                    getAdditionalClassName={getSummaryTableAdditionalClassName}
                    getTagLabel={getTagLabel}
                    size="l"
                    stickyHeader
                    className="workOrderSummaryTable"
                    borderBetweenColumns
                    emptyRowsPlaceholder={<TableLoader requestStatus={workOrderResultStatus} />}
                />
            )}
        </>
    );
};

WorkOrderChangeSummaryTable.displayName = 'WorkOrderChangeSummaryTable';
